import React from 'react'

import { Stack, Box } from '@mui/material'

import logo from 'assets/images/logo.svg'

interface PoweredByVeraProps {
  /**
   * VERA-644 (TD-066): Enterprise white-label entitlement. When true the
   * attribution must not render AT ALL — renders null.
   *
   * This lives in the shared component rather than at each call site on purpose.
   * The entitlement was previously honoured only by the page Footer, so the
   * moment a SECOND surface carried the attribution (the authenticity drawer),
   * a white-labelled brand would have leaked "Powered by Vera" from that surface
   * while their footer correctly hid it. A per-call-site check is one someone
   * forgets; making the component itself refuse to render is not.
   */
  hidden?: boolean
}

/**
 * The "POWERED BY VERA" attribution, linking to vera.inc.
 *
 * Shared because it now appears in two places — the certificate page Footer and
 * the authenticity drawer — and the second hand-roll is where this becomes a
 * component (§36). The two had already drifted before extraction: the footer read
 * "Powered by" in sentence case beside the wordmark, the drawer read
 * "POWERED BY VERA" as 11px uppercase text with the V glyph. Same phrase, two
 * treatments, two sizes, and only one of them honoured the white-label
 * entitlement.
 *
 * Settled on UPPERCASE text at the footer's size, with the real wordmark asset.
 * The wordmark is already uppercase ("VERA"), so sentence-case "Powered by" beside
 * it was the inconsistent part; capitalising the words makes the whole phrase read
 * as one lockup rather than a sentence with a logo stuck on the end. The wordmark
 * stays an image — it is the brand asset, and letterspaced type is an imitation
 * of it, not a substitute.
 */
export const PoweredByVera = ({ hidden }: PoweredByVeraProps = {}) => {
  if (hidden) return null

  return (
    /* One link for the whole phrase: "POWERED BY" and the wordmark are a single
       idea, so splitting the target between them would give two hit areas for one
       thought. Opens in a new tab so someone reading a certificate never loses
       their place. */
    <Stack
      component="a"
      href="https://www.vera.inc"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Powered by Vera. Opens vera.inc in a new tab."
      direction="row"
      alignItems="center"
      sx={{ textDecoration: 'none' }}
    >
      <Box
        sx={theme => ({
          // Size kept at 14 (the label step); WEIGHT and colour carry the
          // demotion instead. This is an attribution, not content — it should be
          // legible to anyone looking for it and invisible to everyone else, and
          // shrinking it below the 14px floor would have made it the one piece
          // of text in the app nobody can read.
          //
          // Demoted, but no longer washed out. It was `grey[400]` at weight 300
          // AND 85% opacity — three demotions stacked on the words while the
          // wordmark beside them renders at full strength in near-black. The
          // phrase and the mark are one lockup, so a large contrast gap between
          // them does not read as "quiet", it reads as two unrelated things, one
          // of which is broken. `text.secondary` keeps it clearly subordinate to
          // the mark while the pair still reads as a unit.
          //
          // 12px, and this is a DELIBERATE exception to the 14px floor in
          // settings/tokens.ts. Fixing the contrast at 14 made a second problem:
          // uppercase, letterspaced, 14px, `text.secondary` is character for
          // character the `SectionLabel` treatment, so the attribution came out
          // looking like a section heading — the same weight in the hierarchy as
          // "HISTORY", which it must never have. The floor exists so no CONTENT
          // is unreadable at arm's length; this is the one string on the page
          // that is explicitly meant to be found only when looked for. Dropping a
          // step is what puts it back below the captions instead of beside them.
          color: theme.palette.text.secondary,
          fontSize: '12px',
          fontWeight: 400,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
        })}
        mx={'8px'}
      >
        Powered by
      </Box>
      <Box
        component="img"
        src={logo}
        alt="VERA"
        sx={theme => ({
          // Scaled with the words (was 62x20 beside 14px type). The lockup only
          // holds if both halves move together — leaving the mark at full size
          // beside 12px type would just re-open the mismatch from the other side.
          width: 53,
          height: 17,
          // Wordmark asset is a fixed dark colour; flip it to white on dark themes
          // so it keeps contrast against a dark background (per theme mode).
          ...(theme.palette.mode === 'dark' && { filter: 'brightness(0) invert(1)' }),
        })}
      />
    </Stack>
  )
}

export default PoweredByVera
